import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Button,
  Collapse,
  Tooltip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { Task, Subtask, Role, TimeLog } from '../types';
import { useProjectStore } from '../store/useProjectStore';
import { getStatusInfo, getRoleColor, formatShortDate, numberToChinese } from '../utils/helpers';
import EditSubtaskModal from './EditSubtaskModal';
import TimeLogModal from './TimeLogModal';

const SubtaskGroup = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const SubtaskRole = styled(Box)(({ theme }) => ({
  fontSize: '0.875rem',
  fontWeight: 600,
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(1),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
}));

const SubtaskItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  padding: theme.spacing(1, 0),
  gap: theme.spacing(1.5),
  marginLeft: theme.spacing(2.5),
  borderBottom: `1px solid ${theme.palette.divider}`,
  position: 'relative',
  '&:last-child': {
    borderBottom: 'none',
  },
  '& .add-subtask-btn': {
    opacity: 0.7,
    transition: 'opacity 0.2s',
  },
  '&:hover .add-subtask-btn': {
    opacity: 1,
  },
}));

const SubtaskContent = styled(Box)({
  flex: 1,
});

const SubtaskText = styled(Typography)<{ completed?: boolean }>(({ completed }) => ({
  fontSize: '0.875rem',
  color: completed ? '#a0aec0' : '#4a5568',
  textDecoration: completed ? 'line-through' : 'none',
  marginBottom: '4px',
  cursor: 'pointer',
  '&:hover': {
    color: completed ? '#718096' : '#2d3748',
    textDecoration: 'underline',
  },
}));

const SubtaskTime = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
  flexWrap: 'wrap',
}));

const StatusIcon = styled(Box)<{ status: string }>(({ status }) => {
  const info = getStatusInfo(status as any);
  return {
    width: 24,
    height: 24,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: 'bold',
    background: info.color,
    color: info.textColor,
    cursor: 'help',
    position: 'relative',
  };
});

const PersonTag = styled(Box)(({ theme }) => ({
  background: '#e7f3ff',
  color: '#0066cc',
  padding: '4px 12px',
  borderRadius: 15,
  fontSize: '0.75rem',
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  marginRight: theme.spacing(0.5),
}));

const ReviewHistory = styled(Box)(({ theme }) => ({
  background: '#fef5e7',
  border: '1px solid #f6ad55',
  borderRadius: 4,
  padding: theme.spacing(1),
  marginTop: theme.spacing(0.5),
  fontSize: '0.7rem',
  color: '#744210',
}));

const SubSubtasksContainer = styled(Box)(({ theme }) => ({
  marginLeft: theme.spacing(3.5),
  marginTop: theme.spacing(1),
  paddingTop: theme.spacing(1),
  borderTop: '1px dashed #e2e8f0',
}));

const SubSubtaskItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  padding: theme.spacing(0.75, 0),
  gap: theme.spacing(1),
  fontSize: '0.8rem',
}));

const SubSubtaskStatus = styled(Box)<{ completed: boolean }>(({ completed }) => ({
  width: 18,
  height: 18,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '10px',
  fontWeight: 'bold',
  background: completed ? '#48bb78' : '#e2e8f0',
  color: completed ? 'white' : '#a0aec0',
  cursor: 'pointer',
}));

interface SubtaskListProps {
  taskId: string;
  task: Task;
  subtasks: Subtask[];
  onOpenDiscussion: (subtaskId: string) => void;
}

const SubtaskList: React.FC<SubtaskListProps> = ({ taskId, task, subtasks, onOpenDiscussion }) => {
  const { toggleSubSubtask, addSubSubtask, addTimeLog, updateTimeLog, deleteTimeLog } = useProjectStore();
  const [expandedSubtasks, setExpandedSubtasks] = useState<Set<string>>(new Set());
  const [addSubSubtaskDialog, setAddSubSubtaskDialog] = useState<{
    open: boolean;
    subtaskId: string;
    subtaskText: string;
    text: string;
  }>({ open: false, subtaskId: '', subtaskText: '', text: '' });
  const [showInlineInput, setShowInlineInput] = useState<string | null>(null);
  const [inlineInputText, setInlineInputText] = useState('');
  const [editSubtaskModal, setEditSubtaskModal] = useState<{
    open: boolean;
    subtask: Subtask | null;
  }>({ open: false, subtask: null });
  const [timeLogModal, setTimeLogModal] = useState<{
    open: boolean;
    subtask: Subtask | null;
    subSubtaskId: string | null;
  }>({ open: false, subtask: null, subSubtaskId: null });

  const toggleSubtaskExpand = (subtaskId: string) => {
    setExpandedSubtasks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(subtaskId)) {
        newSet.delete(subtaskId);
      } else {
        newSet.add(subtaskId);
      }
      return newSet;
    });
  };

  const handleConfirmAddSubSubtask = () => {
    if (addSubSubtaskDialog.text.trim()) {
      addSubSubtask(taskId, addSubSubtaskDialog.subtaskId, addSubSubtaskDialog.text);
      setAddSubSubtaskDialog({ open: false, subtaskId: '', subtaskText: '', text: '' });
    }
  };

  const handleInlineAddSubSubtask = (subtaskId: string) => {
    if (inlineInputText.trim()) {
      addSubSubtask(taskId, subtaskId, inlineInputText);
      setShowInlineInput(null);
      setInlineInputText('');
      // Auto expand to show the new sub-subtask
      setExpandedSubtasks(prev => new Set(prev).add(subtaskId));
    }
  };

  const handleEditSubtask = (subtask: Subtask) => {
    setEditSubtaskModal({ open: true, subtask });
  };

  const handleCloseEditModal = () => {
    setEditSubtaskModal({ open: false, subtask: null });
  };

  const handleOpenTimeLog = (subtask: Subtask, subSubtaskId?: string) => {
    setTimeLogModal({ open: true, subtask, subSubtaskId: subSubtaskId || null });
  };

  const handleCloseTimeLogModal = () => {
    setTimeLogModal({ open: false, subtask: null, subSubtaskId: null });
  };

  const handleAddTimeLog = (timeLog: Omit<TimeLog, 'id' | 'createdAt'>) => {
    if (timeLogModal.subtask) {
      addTimeLog(task.id, timeLogModal.subtask.id, timeLogModal.subSubtaskId, timeLog);
    }
  };

  const handleUpdateTimeLog = (timeLogId: string, updates: Partial<TimeLog>) => {
    if (timeLogModal.subtask) {
      updateTimeLog(task.id, timeLogModal.subtask.id, timeLogModal.subSubtaskId, timeLogId, updates);
    }
  };

  const handleDeleteTimeLog = (timeLogId: string) => {
    if (timeLogModal.subtask) {
      deleteTimeLog(task.id, timeLogModal.subtask.id, timeLogModal.subSubtaskId, timeLogId);
    }
  };

  const getCurrentTimeLogs = () => {
    if (!timeLogModal.subtask) return [];
    
    if (timeLogModal.subSubtaskId) {
      const subSubtask = timeLogModal.subtask.subSubtasks.find(sst => sst.id === timeLogModal.subSubtaskId);
      return subSubtask?.timeLogs || [];
    }
    return timeLogModal.subtask.timeLogs || [];
  };

  // Get all unique assignees for the autocomplete
  const allAssignees = Array.from(new Set(
    subtasks.flatMap(st => st.assignees || [])
  ));

  // Group subtasks by role
  const subtasksByRole = subtasks.reduce((acc, subtask) => {
    if (!acc[subtask.role]) {
      acc[subtask.role] = [];
    }
    acc[subtask.role].push(subtask);
    return acc;
  }, {} as Record<Role, Subtask[]>);

  return (
    <Box>
      {Object.entries(subtasksByRole).map(([role, roleSubtasks]) => (
        <SubtaskGroup key={role}>
          <SubtaskRole>
            <Chip
              label={role}
              size="small"
              sx={{
                background: getRoleColor(role),
                color: 'white',
                height: 20,
              }}
            />
            <Typography variant="caption" color="text.secondary">
              {roleSubtasks.filter(s => s.status === 'completed').length}/{roleSubtasks.length} 完成
            </Typography>
          </SubtaskRole>

          {roleSubtasks.map((subtask) => {
            const statusInfo = getStatusInfo(subtask.status);
            const hasSubSubtasks = subtask.subSubtasks && subtask.subSubtasks.length > 0;
            const isExpanded = expandedSubtasks.has(subtask.id);

            return (
              <SubtaskItem key={subtask.id}>
                <Tooltip title={statusInfo.text} placement="top" arrow>
                  <StatusIcon status={subtask.status}>
                    {statusInfo.icon}
                  </StatusIcon>
                </Tooltip>

                <SubtaskContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SubtaskText 
                      completed={subtask.status === 'completed'}
                      onClick={() => handleEditSubtask(subtask)}
                    >
                      {subtask.text}
                      {subtask.reviewHistory && subtask.reviewHistory.length > 0 && (
                        <Chip
                          label={`(${numberToChinese((subtask.reviewHistory?.length || 0) + 1)})`}
                          size="small"
                          sx={{ ml: 0.5, fontSize: '0.65rem', height: 16 }}
                        />
                      )}
                    </SubtaskText>
                    {hasSubSubtasks && (
                      <>
                        <Chip
                          label={`${subtask.subSubtasks!.filter(s => s.completed).length}/${subtask.subSubtasks!.length} 細項`}
                          size="small"
                          sx={{
                            fontSize: '0.7rem',
                            height: 20,
                            bgcolor: subtask.subSubtasks!.every(s => s.completed) ? 'success.lighter' : 'action.hover',
                            color: subtask.subSubtasks!.every(s => s.completed) ? 'success.main' : 'text.secondary',
                          }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => toggleSubtaskExpand(subtask.id)}
                          sx={{ p: 0.25 }}
                        >
                          {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </IconButton>
                      </>
                    )}
                  </Box>

                  <Box sx={{ mt: 1, mb: 0.5 }}>
                    {subtask.assignees.length > 0 ? (
                      subtask.assignees.map(assignee => (
                        <PersonTag key={assignee}>
                          <Box sx={{
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            background: '#667eea',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.65rem',
                            fontWeight: 600,
                          }}>
                            {assignee.charAt(0)}
                          </Box>
                          {assignee}
                        </PersonTag>
                      ))
                    ) : (
                      <Typography variant="caption" color="text.secondary">尚未指派</Typography>
                    )}
                  </Box>

                  {/* Progress bar for sub-subtasks */}
                  {hasSubSubtasks && subtask.subSubtasks!.length > 1 && (
                    <Box sx={{ mt: 1, mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={(subtask.subSubtasks!.filter(s => s.completed).length / subtask.subSubtasks!.length) * 100}
                          sx={{
                            flex: 1,
                            height: 6,
                            borderRadius: 3,
                            backgroundColor: 'action.hover',
                            '& .MuiLinearProgress-bar': {
                              borderRadius: 3,
                              backgroundColor: subtask.subSubtasks!.every(s => s.completed) ? 'success.main' : 'primary.main',
                            },
                          }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 45 }}>
                          {Math.round((subtask.subSubtasks!.filter(s => s.completed).length / subtask.subSubtasks!.length) * 100)}%
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  <SubtaskTime>
                    {subtask.startDate && (
                      <Box>
                        <span style={{ color: '#a0aec0' }}>開始: </span>
                        <span>{formatShortDate(subtask.startDate)}</span>
                      </Box>
                    )}
                    {subtask.expectedDate && (
                      <Box>
                        <span style={{ color: '#a0aec0' }}>預計: </span>
                        <span>{formatShortDate(subtask.expectedDate)}</span>
                      </Box>
                    )}
                    {subtask.actualCompletedDate ? (
                      <Box>
                        <span style={{ color: '#a0aec0' }}>完成: </span>
                        <span style={{ color: '#38a169' }}>{formatShortDate(subtask.actualCompletedDate)}</span>
                      </Box>
                    ) : (
                      <Box>
                        <span style={{ color: '#a0aec0' }}>完成: </span>
                        <span>{subtask.status === 'completed' ? '已完成' : '未完成'}</span>
                      </Box>
                    )}
                  </SubtaskTime>

                  {subtask.reviewHistory && subtask.reviewHistory.length > 0 && (
                    <ReviewHistory>
                      {subtask.reviewHistory.map((review, index) => (
                        <Box key={review.id} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5 }}>
                          <span>❌</span>
                          <span>第{numberToChinese(review.reviewCount || 1)}次驗收失敗 ({formatShortDate(review.date)}) - {review.reason}</span>
                        </Box>
                      ))}
                    </ReviewHistory>
                  )}

                  <Collapse in={isExpanded}>
                    {hasSubSubtasks && (
                      <SubSubtasksContainer>
                        {subtask.subSubtasks!.map((subSubtask) => (
                          <SubSubtaskItem key={subSubtask.id}>
                            <Tooltip 
                              title={subSubtask.completed ? '已完成 | 點擊取消完成' : '待執行 | 點擊標記為完成'} 
                              placement="top"
                            >
                              <SubSubtaskStatus
                                completed={subSubtask.completed}
                                onClick={() => toggleSubSubtask(taskId, subtask.id, subSubtask.id)}
                              >
                                {subSubtask.completed ? '✓' : '○'}
                              </SubSubtaskStatus>
                            </Tooltip>
                            <Typography
                              variant="caption"
                              sx={{
                                color: subSubtask.completed ? '#a0aec0' : '#4a5568',
                                textDecoration: subSubtask.completed ? 'line-through' : 'none',
                                flex: 1,
                              }}
                            >
                              {subSubtask.text}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenTimeLog(subtask, subSubtask.id)}
                              sx={{ 
                                p: 0.25,
                                ml: 1,
                                '& .MuiSvgIcon-root': {
                                  fontSize: '1rem'
                                }
                              }}
                              title="工時記錄"
                            >
                              <AccessTimeIcon />
                              {subSubtask.timeLogs && subSubtask.timeLogs.length > 0 && (
                                <Chip
                                  label={subSubtask.timeLogs.length}
                                  size="small"
                                  sx={{
                                    position: 'absolute',
                                    top: -2,
                                    right: -2,
                                    fontSize: '0.5rem',
                                    height: 14,
                                    minWidth: 14,
                                    '& .MuiChip-label': {
                                      px: 0.5,
                                    }
                                  }}
                                />
                              )}
                            </IconButton>
                          </SubSubtaskItem>
                        ))}
                      </SubSubtasksContainer>
                    )}
                  </Collapse>

                  {/* Inline Add Sub-subtask Input */}
                  {showInlineInput === subtask.id && (
                    <Box sx={{ mt: 2, ml: 3.5 }}>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="輸入細項任務描述..."
                          value={inlineInputText}
                          onChange={(e) => setInlineInputText(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              handleInlineAddSubSubtask(subtask.id);
                            }
                          }}
                          autoFocus
                          sx={{ fontSize: '0.875rem' }}
                        />
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => handleInlineAddSubSubtask(subtask.id)}
                        >
                          新增
                        </Button>
                        <Button
                          size="small"
                          onClick={() => {
                            setShowInlineInput(null);
                            setInlineInputText('');
                          }}
                        >
                          取消
                        </Button>
                      </Box>
                    </Box>
                  )}
                </SubtaskContent>

                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                  <IconButton
                    size="small"
                    onClick={() => handleOpenTimeLog(subtask)}
                    sx={{ p: 0.5 }}
                    title="工時記錄"
                  >
                    <AccessTimeIcon fontSize="small" />
                    {subtask.timeLogs?.length > 0 && (
                      <Chip
                        label={subtask.timeLogs.length}
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: -4,
                          right: -4,
                          fontSize: '0.6rem',
                          height: 16,
                          minWidth: 16,
                        }}
                      />
                    )}
                  </IconButton>
                  <Button
                    className="add-subtask-btn"
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon sx={{ fontSize: '0.8rem' }} />}
                    onClick={() => setShowInlineInput(subtask.id)}
                    sx={{
                      minWidth: 'auto',
                      fontSize: '0.7rem',
                      p: '2px 8px',
                      borderColor: 'divider',
                      '&:hover': { 
                        borderColor: 'primary.main',
                        bgcolor: 'primary.lighter',
                      },
                    }}
                  >
                    新增細項
                  </Button>
                  <IconButton
                    size="small"
                    onClick={() => onOpenDiscussion(subtask.id)}
                    sx={{ p: 0.5 }}
                  >
                    <ChatBubbleOutlineIcon fontSize="small" />
                    {subtask.discussionCount > 0 && (
                      <Chip
                        label={subtask.discussionCount}
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: -4,
                          right: -4,
                          fontSize: '0.6rem',
                          height: 16,
                          minWidth: 16,
                        }}
                      />
                    )}
                  </IconButton>
                </Box>
              </SubtaskItem>
            );
          })}
        </SubtaskGroup>
      ))}

      {/* Add Sub-subtask Dialog */}
      <Dialog 
        open={addSubSubtaskDialog.open} 
        onClose={() => setAddSubSubtaskDialog({ open: false, subtaskId: '', subtaskText: '', text: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>新增細項任務</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              為「{addSubSubtaskDialog.subtaskText}」新增細項
            </Typography>
          </Box>
          <TextField
            fullWidth
            label="細項描述"
            placeholder="請輸入細項任務的描述..."
            value={addSubSubtaskDialog.text}
            onChange={(e) => setAddSubSubtaskDialog(prev => ({ ...prev, text: e.target.value }))}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleConfirmAddSubSubtask();
              }
            }}
            autoFocus
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddSubSubtaskDialog({ open: false, subtaskId: '', subtaskText: '', text: '' })}>
            取消
          </Button>
          <Button variant="contained" onClick={handleConfirmAddSubSubtask}>
            新增
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Subtask Modal */}
      {editSubtaskModal.subtask && (
        <EditSubtaskModal
          open={editSubtaskModal.open}
          onClose={handleCloseEditModal}
          task={task}
          subtask={editSubtaskModal.subtask}
          allAssignees={allAssignees}
        />
      )}

      {/* Time Log Modal */}
      {timeLogModal.subtask && (
        <TimeLogModal
          open={timeLogModal.open}
          onClose={handleCloseTimeLogModal}
          taskTitle={task.title}
          subtaskText={timeLogModal.subtask.text}
          subSubtaskText={timeLogModal.subSubtaskId ? 
            timeLogModal.subtask.subSubtasks.find(sst => sst.id === timeLogModal.subSubtaskId)?.text : 
            undefined
          }
          assignees={timeLogModal.subtask.assignees}
          timeLogs={getCurrentTimeLogs()}
          onAddTimeLog={handleAddTimeLog}
          onUpdateTimeLog={handleUpdateTimeLog}
          onDeleteTimeLog={handleDeleteTimeLog}
        />
      )}
    </Box>
  );
};

export default SubtaskList; 